import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { LogoLoader } from '@/components/logo-loader'
import React,{ useState } from 'react'
import { motion } from 'framer-motion'
import { Lock,Mail,Loader2,AlertCircle,ArrowLeft,CheckCircle2 } from 'lucide-react'
import { createFileRoute,useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card,CardContent,CardHeader,CardTitle } from '@/components/ui/card'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { queryKeys } from '@/lib/query-keys'
import type { OrganizationSlugLookup } from '@/types'
import { authManager,requireGuest } from '@/lib/auth'
import { clearSelectedStore } from '@/features/shared'

export const Route = createFileRoute('/auth/$slug')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: TenantLoginPage,
  beforeLoad: requireGuest('/'),
})

function TenantLoginPage() {
  const navigate = useNavigate()
  const { slug } = Route.useParams()

  const [loading, setLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [view, setView] = useState<'login' | 'forgot' | 'sent'>('login')
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotError, setForgotError] = useState<string | null>(null)

  const [formData, setFormData] = useState({ email: '', password: '' })

  const orgQuery = useQuery({
    queryKey: queryKeys.orgBySlug(slug),
    queryFn: () => api.get<OrganizationSlugLookup>(`/organizations/slug/${slug}`),
    enabled: !!slug,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const org = orgQuery.data
  const isValidating = orgQuery.isLoading
  const orgError =
    !isValidating && (orgQuery.isError || !org?.id) ? 'Organization not found' : null
  const resolvedOrgId = org?.id ?? null
  const orgName = org?.displayName || org?.slug || null
  const orgLogo = org?.avatarUrl ?? null

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setLoginError(null)
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotLoading(true)
    setForgotError(null)
    try {
      await api.post(`/auth/forgot-password`,
        { email: forgotEmail || formData.email, organizationId: resolvedOrgId },
        { headers: { 'x-skip-auth-redirect': '1' } }
      )
      setView('sent')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      setForgotError(err.response?.data?.message || 'Something went wrong. Please try again.')
    } finally {
      setForgotLoading(false)
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setLoginError(null)

    try {
      const payload = {
        organizationId: resolvedOrgId,
        email: formData.email,
        password: formData.password,
      }

      const response = await api.post(`/auth/login`, payload, {
        headers: { 'x-skip-auth-redirect': '1' },
      })
      const data = response as Record<string, unknown>

      authManager.login(data, resolvedOrgId || undefined)
      // Remember the org so logout returns here (/auth/:slug) instead of the
      // org-name entry step.
      authManager.setLastOrgSlug(slug)
      // Force a fresh store choice on every login: clear any previously persisted
      // selection so the dashboard shows the store-selection modal (multi-store)
      // instead of silently reusing the last-used store.
      clearSelectedStore()
      navigate({ to: '/' })
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } } }
      setLoginError(err.response?.data?.message || 'Invalid email or password')
    } finally {
      setLoading(false)
    }
  }

  if (isValidating) {
    return <LogoLoader fullScreen className="h-[120px] w-[120px]" />
  }

  if (orgError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-4">
        <Card className="w-full max-w-md text-center p-6">
          <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <h2 className="text-xl font-bold mb-2">Organization Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The organization slug &quot;{slug}&quot; does not exist.
          </p>
          <Button onClick={() => navigate({ to: '/' })}>Return to Login</Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-xl">
          <CardHeader className="space-y-4 text-center">
            <motion.div layout className="mx-auto w-16 h-16 flex items-center justify-center overflow-hidden">
              {orgLogo ? (
                <img
                  src={orgLogo}
                  alt="Organization Logo"
                  className="w-full h-full object-cover rounded-2xl"
                />
              ) : (
                <>
                  <img
                    src="https://cdn.rootpoint.io/platform/logo-color.svg"
                    alt="RootPoint Logo"
                    className="w-full h-full object-contain dark:hidden"
                  />
                  <img
                    src="https://cdn.rootpoint.io/platform/logo-color.svg"
                    alt="RootPoint Logo"
                    className="w-full h-full object-contain hidden dark:block"
                  />
                </>
              )}
            </motion.div>
            <div>
              <CardTitle className="text-3xl font-bold text-balance">
                {orgName || 'RootPoint'}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                {view === 'forgot'
                  ? 'Reset your password'
                  : view === 'sent'
                    ? 'Check your email'
                    : 'Sign in to your account'}
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={view === 'forgot' ? handleForgotPassword : handleLogin}
              className="space-y-6"
            >
              {view === 'login' && (
                <div className="space-y-4 px-1 py-1 -mx-1">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="pl-10 h-11 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary"
                        placeholder="name@company.com"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <button
                        type="button"
                        className="text-sm text-primary hover:underline"
                        onClick={() => {
                          setForgotEmail(formData.email)
                          setForgotError(null)
                          setView('forgot')
                        }}
                      >
                        Forgot?
                      </button>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input
                        id="password"
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="pl-10 h-11 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </div>
              )}

              {view === 'forgot' && (
                <div className="space-y-4 px-1 py-1 -mx-1">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email Address</Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <Input
                        id="forgot-email"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => {
                          setForgotEmail(e.target.value)
                          setForgotError(null)
                        }}
                        className="pl-10 h-11 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-primary"
                        placeholder="name@company.com"
                        autoFocus
                      />
                    </div>
                  </div>
                  {forgotError && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-destructive font-medium"
                    >
                      {forgotError}
                    </motion.p>
                  )}
                </div>
              )}

              {view === 'sent' && (
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                  <p className="font-medium">Reset link sent!</p>
                  <p className="text-sm text-muted-foreground">
                    Check your inbox for a password reset link. It may take a few minutes.
                  </p>
                </div>
              )}

              {view === 'sent' ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => {
                    setView('login')
                    setForgotEmail('')
                    setForgotError(null)
                  }}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Sign In
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button
                    type="submit"
                    className="w-full h-11 transition-all"
                    disabled={loading || forgotLoading}
                  >
                    {loading || forgotLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {view === 'forgot' ? 'Sending...' : 'Signing in...'}
                      </>
                    ) : view === 'forgot' ? (
                      'Send Reset Link'
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                  {view === 'forgot' && (
                    <button
                      type="button"
                      className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center pt-1"
                      onClick={() => {
                        setView('login')
                        setForgotEmail('')
                        setForgotError(null)
                      }}
                    >
                      <span className="flex items-center justify-center gap-1">
                        <ArrowLeft className="h-3 w-3" /> Back to Sign In
                      </span>
                    </button>
                  )}
                </div>
              )}

              {loginError && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-destructive font-medium text-center"
                >
                  {loginError}
                </motion.p>
              )}
            </form>
          </CardContent>
        </Card>
      </motion.div>
      <div className="mt-8 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()} RootPoint. All rights reserved.
      </div>
    </div>
  )
}
